// Everything the left rail can hold: its name, what its button says, its key
// and what it does. The rail and its editor both read this; which ones show,
// and in what order, is the layout in state/rail.ts.

import type { ReactNode } from "react";
import { ArrowUpRight, Brush, Circle, Database, Eraser, Keyboard, LayoutGrid, Maximize, Maximize2, MousePointer2, Palette, Pencil, Plus, Square, Type, type LucideIcon } from "lucide-react";
import { SPEC } from "../../shared/catalog";
import { addWidget } from "../state/actions";
import { boardItems, get, set, type Panel, type Tool } from "../state/store";
import { QUICK_ADDS, type QuickAdd, type RailId } from "../state/rail";
import { camera } from "../canvas/camera";
import { bounds, itemRect } from "../canvas/geometry";
import { enterFullscreen } from "./Fullscreen";
import { openOverview } from "./Panels";
import { WidgetIcon } from "./icons";

export interface RailItem {
  /** its name in the editor */
  label: string;
  /** what its button says, when that is more than the name */
  tip?: string;
  kbd?: string;
  icon: (size: number) => ReactNode;
  run: () => void;
  active?: (s: { tool: Tool; panel: Panel }) => boolean;
}

/** The six drawing tools, behind the rail's one Draw button. */
export const DRAW_TOOLS: { tool: Tool; label: string; kbd: string; Icon: LucideIcon }[] = [
  { tool: "pen", label: "Draw", kbd: "P", Icon: Pencil },
  { tool: "rect", label: "Box", kbd: "R", Icon: Square },
  { tool: "ellipse", label: "Circle", kbd: "O", Icon: Circle },
  { tool: "arrow", label: "Arrow", kbd: "L", Icon: ArrowUpRight },
  { tool: "text", label: "Label", kbd: "T", Icon: Type },
  { tool: "eraser", label: "Eraser", kbd: "E", Icon: Eraser },
];

export const drawTool = (tool: Tool) => DRAW_TOOLS.find((t) => t.tool === tool) ?? null;

export function pickTool(tool: Tool) {
  set({ tool, editing: null, ...(tool !== "select" ? { selection: [] } : {}) });
}

const togglePanel = (panel: Exclude<Panel, null>) => () => set((s) => ({ panel: s.panel === panel ? null : panel, panelArg: null }));

const ADD_TIP: Record<QuickAdd, string> = {
  sticky: "Add a sticky note",
  timer: "Add a focus timer",
  clock: "Add a clock",
  tasks: "Add a task list",
  notes: "Add notes",
  calendar: "Add a calendar",
  countdown: "Add a countdown",
  habits: "Add a habit tracker",
  links: "Add links",
  heading: "Add a heading",
  weather: "Add the weather",
};

function quickAdd(type: QuickAdd): RailItem {
  return {
    label: SPEC[type].label,
    tip: ADD_TIP[type],
    kbd: type === "sticky" ? "N" : undefined,
    icon: (size) => <WidgetIcon type={type} size={size} strokeWidth={2} />,
    // a heading says what it is for, as it does from the gallery
    run: () => void addWidget(type, type === "heading" ? { props: { text: "New section" } } : {}),
  };
}

const icon =
  (I: LucideIcon) =>
  (size: number): ReactNode => <I size={size} />;

export const RAIL_ITEMS: Record<RailId, RailItem> = {
  select: { label: "Select", kbd: "V", icon: icon(MousePointer2), run: () => pickTool("select"), active: (s) => s.tool === "select" },
  draw: { label: "Draw", kbd: "P", icon: icon(Brush), run: () => {}, active: (s) => !!drawTool(s.tool) },
  gallery: { label: "Add a widget", kbd: "A", icon: (size) => <Plus size={size + 1} />, run: togglePanel("gallery"), active: (s) => s.panel === "gallery" },
  ...(Object.fromEntries(QUICK_ADDS.map((t) => [`add:${t}`, quickAdd(t)])) as Record<`add:${QuickAdd}`, RailItem>),
  data: { label: "Data and sources", kbd: "D", icon: icon(Database), run: togglePanel("data"), active: (s) => s.panel === "data" },
  boards: { label: "All boards", kbd: "G", icon: icon(LayoutGrid), run: () => openOverview(), active: (s) => s.panel === "boards" },
  look: { label: "Look and themes", icon: icon(Palette), run: togglePanel("theme"), active: (s) => s.panel === "theme" },
  fit: { label: "See everything", kbd: "⇧1", icon: icon(Maximize), run: () => camera.fit(bounds(boardItems(get()).map(itemRect)), { maxZ: 1 }) },
  fullscreen: { label: "Full screen", kbd: "F", icon: icon(Maximize2), run: () => void enterFullscreen() },
  shortcuts: { label: "Keyboard shortcuts", kbd: "?", icon: icon(Keyboard), run: togglePanel("shortcuts"), active: (s) => s.panel === "shortcuts" },
};

export const GROUP_LABEL = { tools: "Tools", add: "Add", board: "Board" } as const;
